// src/contexts/TenantContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext"; // Assuming you have an AuthContext to get the current user
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase"; // Assuming you have initialized Firestore in a firebase.ts file
import { Tenant } from "../models/Tenant";
import { getSubdomainFromUrl } from "../utils/url";
import { TenantService } from "../services/TenantService";
import { TeamMember } from "../models/TeamMember";

// Define the TenantContext type
interface TenantContextType {
  currentTeamMember?: TeamMember | undefined;
  tenant: Tenant | undefined;
  loading: boolean;
  error: string | null;
  updateTenant: (
    tenantId: string,
    updatedData: Partial<Omit<Tenant, "_id">>,
  ) => Promise<void>;
}

// Create the context
const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Create a provider component
export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [tenant, setTenant] = useState<Tenant | undefined>(
    localStorage.getItem("tenant")
      ? JSON.parse(localStorage.getItem("tenant")!)
      : null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenant) {
      setLoading(false);
      return; // If tenant is already loaded, skip fetching
    }
    const tenantId = getSubdomainFromUrl(window.location.href);
    const docRef = doc(collection(db, "tenants"), tenantId);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        setLoading(false);
        if (docSnapshot.exists()) {
          setTenant({ _id: docSnapshot.id, ...docSnapshot.data() } as Tenant);
          localStorage.setItem(
            "tenant",
            JSON.stringify({ id: docSnapshot.id, ...docSnapshot.data() }),
          );
        } else {
          setError("Tenant not found.");
        }
      },
      (error) => {
        setLoading(false);
        setError(error.message);
      },
    );
    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const teamMembers = Object.entries(tenant?.teamMembers || {}).map(
    ([id, teamMember]) => ({
      ...teamMember,
      id,
    }),
  );
  const currentTeamMember =
    teamMembers.find((p) => p._id === user?.uid) || undefined;

  const updateTenant = async (
    tenantId: string,
    updatedData: Partial<Omit<Tenant, "_id">>,
  ) => {
    try {
      await TenantService.updateTenant(tenantId, updatedData);
      const updatedTenant = await TenantService.getTenant();
      setTenant(updatedTenant!);
    } catch (error) {
      console.error("Error updating tenant:", error);
    }
  };

  return (
    <TenantContext.Provider
      value={{ currentTeamMember, tenant, loading, error, updateTenant }}
    >
      {children}
    </TenantContext.Provider>
  );
};

// Custom hook for using the TenantContext
export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenant must be used within an TenantProvider");
  }
  return context;
};
